import React, { createContext, useContext, useEffect, useState } from 'react';

const translations = {
  en: {
    // top bar
    'top.home': 'Home',
    'top.wysetribe': 'WYSE Tribe',
    'top.locations': 'Calgary · Toronto',
    'top.signIn': 'Sign in',
    'top.dashboard': 'My dashboard',
    'top.signOut': 'Sign out',
    'top.language': 'Language',
    // header
    'brand.sub': 'Accounting · Tax Advisory',
    'nav.home': 'Home',
    'nav.about': 'About Us',
    'nav.services': 'Services',
    'nav.specialists': 'Specialists',
    // Phase 3 — Specialist directory + profile
    'spec.eyebrow': 'Our Tax Specialists',
    'spec.heroTitle': 'Meet the people who’ll handle your',
    'spec.heroAccent': 'tax season',
    'spec.heroSubtitle': 'Browse our bilingual roster of CRA-trusted Tax Specialists. Filter by the language you’d like to work in, then book directly with the one that fits.',
    'spec.searchPlaceholder': 'Search by name, title, or bio…',
    'spec.languageLabel': 'Language',
    'spec.langAny': 'Any',
    'spec.empty': 'No specialists match those filters yet.',
    'spec.viewProfile': 'View profile',
    'spec.backToDirectory': 'Back to all specialists',
    'spec.titleFallback': 'Tax Specialist',
    'spec.bookWith': 'Book with',
    'spec.about': 'About',
    'spec.certifications': 'Certifications & memberships',
    'spec.services': 'Services they offer',
    'spec.workingHours': 'Working hours',
    'spec.workingHoursDesc': 'Mon–Fri 9 AM – 5 PM',
    'spec.workingHoursHint': 'Book online to see live availability.',
    'spec.preferTitle': 'Prefer this specialist?',
    'spec.preferDesc': 'You can mark them as your preferred specialist from your member account.',
    'spec.managePrefs': 'Manage preferences',
    'spec.notFound': 'Specialist not found',
    'spec.notFoundDesc': 'This profile may have moved or been deactivated.',
    'nav.cases': 'Cases',
    'nav.blog': 'Blog',
    'nav.contact': 'Contact',
    'nav.pages': 'Pages',
    'cta.bookAppointment': 'Book Appointment',
    'cta.getStarted': 'Get Started',
    'cta.callUs': 'Call Us Now',
    'cta.contactUs': 'Contact Us',
    'cta.allServices': 'All Services',
    'cta.allArticles': 'All Articles',
    'cta.learnMore': 'Learn more',
    'cta.talkToAdvisor': 'Talk to an advisor',
    'cta.readArticle': 'Read article',
    'cta.submitNow': 'Submit Now',
    'cta.submitted': 'Submitted',
    'cta.watchStory': 'Watch our story',

    // hero
    'hero.eyebrow': 'Welcome to WYSE Tribe',
    'hero.titlePre': 'Tax is easier',
    'hero.titleBreak': 'than you',
    'hero.titleAccent': 'think',
    'hero.desc':
      'Personal, corporate and cross-border tax handled by Tax Specialists who actually pick up the phone. WYSE Tribe pairs year-round advisory with cloud-first filing – so you keep more of what you earn.',
    'hero.trusted': 'Trusted by 4,200+ Canadian filers',

    // highlights
    'hl.expert.title': 'Expert Advisors',
    'hl.expert.text': 'Tax specialists with 18+ years of Canadian tax expertise.',
    'hl.consulting.title': 'Tax Consulting',
    'hl.consulting.text': 'Personal, corporate and cross-border tax planning that maximizes returns.',
    'hl.planning.title': 'Financial Planning',
    'hl.planning.text': 'Holistic wealth strategies built around your goals and life stage.',

    // about
    'about.eyebrow': 'We are tax consultants',
    'about.titlePre': 'We provide the best service for your',
    'about.titleAccent': 'convenience',
    'about.desc':
      'WYSE Tribe was founded on a simple idea: tax should be a tool that compounds wealth, not a once-a-year scramble. Our Tax Specialists combine deep CRA expertise with a cloud-first workflow so every client gets clear answers, real planning and zero surprises.',
    'about.advisors': '32+ certified advisors',
    'about.cpaLine': 'CRA Authorized',
    'about.b.cra': 'CRA-authorized specialists',
    'about.b.pricing': 'Transparent fixed pricing',
    'about.b.year': 'Year-round tax support',
    'about.b.cross': 'Cross-border expertise',
    'about.b.audit': 'Audit representation',
    'about.b.secure': 'Confidential & secure',
    'about.founder': 'Founder · CPA',

    // stats
    'stats.clients': 'Happy Clients',
    'stats.returns': 'Returns Filed',
    'stats.years': 'Years of Experience',
    'stats.experts': 'Tax Experts',

    // comfort
    'comfort.titlePre': 'Our plan makes you feel',
    'comfort.titleAccent': 'more comfortable',
    'comfort.titlePost': 'in tax management',
    'comfort.desc':
      'From the first onboarding call to your post-filing review, every step is mapped out. You\u2019ll always know what\u2019s happening with your tax \u2013 and what\u2019s coming next.',
    'comfort.f1.t': 'Dedicated Tax Specialist',
    'comfort.f1.d': 'One advisor who knows your file inside-out.',
    'comfort.f2.t': 'Year-round support',
    'comfort.f2.d': 'Quarterly check-ins, not just tax season.',
    'comfort.f3.t': 'CRA representation',
    'comfort.f3.d': 'We handle every letter, review and audit.',

    // services
    'serv.eyebrow': 'Services',
    'serv.titlePre': 'Our services',
    'serv.titleAccent': 'include',
    'serv.desc':
      'Personal returns, corporate filings, audit defence and cross-border specialty work \u2013 all under one roof, all year round.',
    'serv.pageEyebrow': 'What we do',
    'serv.pageTitlePre': 'Every service your tax life',
    'serv.pageTitleAccent': 'needs',
    'serv.pageDesc':
      'From a simple T1 to multi-entity, multi-country structures \u2013 WYSE Tribe handles it with the same care.',

    // service cards (per service id)
    'service.personal-tax.title': 'Personal Tax Filing',
    'service.personal-tax.short': 'Maximise refunds with accurate, CRA-compliant personal returns.',
    'service.personal-tax.desc': 'From T4 employees to self-employed contractors, we handle every Canadian personal return with care. We identify every eligible deduction and credit so you keep more of what you earn.',
    'service.corporate-tax.title': 'Corporate Tax',
    'service.corporate-tax.short': 'T2 filings, year-end planning and tax-efficient structures.',
    'service.corporate-tax.desc': 'Strategic corporate tax planning, T2 returns, GST/HST filings and CRA correspondence handled end-to-end so your business can focus on growth.',
    'service.audit-assurance.title': 'Audit & Assurance',
    'service.audit-assurance.short': 'Confident CRA audit representation and reviewed financials.',
    'service.audit-assurance.desc': 'Sleep easy knowing our audit specialists represent you directly with the CRA. We prepare documentation, negotiate outcomes and protect your position.',
    'service.bookkeeping.title': 'Bookkeeping',
    'service.bookkeeping.short': 'Clean books, monthly reports and cloud-first workflows.',
    'service.bookkeeping.desc': 'Monthly bookkeeping with QuickBooks and Xero integration, payroll, bank reconciliations and management reports that actually make sense.',
    'service.cross-border.title': 'Cross-Border Tax',
    'service.cross-border.short': 'Canada\u2013US returns, FBAR and treaty optimization.',
    'service.cross-border.desc': 'US\u2013Canada cross-border filings, FBAR, FATCA, treaty positions and expat planning. We make dual filings simple and compliant.',
    'service.financial-planning.title': 'Financial Planning',
    'service.financial-planning.short': 'RRSP, TFSA, retirement and estate strategies that compound.',
    'service.financial-planning.desc': 'A complete plan covering investments, retirement, insurance and estate \u2013 aligned with your tax position so every dollar works harder.',

    // case studies
    'case.cat.corporate': 'Corporate',
    'case.cat.family-office': 'Family Office',
    'case.cat.personal': 'Personal',
    'case.cat.cross-border': 'Cross-Border',
    'case.short-range-tax-planning.title': 'Short-Range Tax Planning',
    'case.short-range-tax-planning.summary': 'Year-end optimization for a Toronto-based agency, reducing tax payable by 28% in a single fiscal year.',
    'case.purposive-tax-planning.title': 'Purposive Tax Planning',
    'case.purposive-tax-planning.summary': 'Structured a family trust and holding company for a multi-generational client to compound wealth tax-efficiently.',
    'case.permissive-tax-planning.title': 'Permissive Tax Planning',
    'case.permissive-tax-planning.summary': 'Leveraged every CRA-allowed credit and deduction for a self-employed creator, increasing refund by $14,800.',
    'case.cross-border-restructure.title': 'Cross-Border Restructure',
    'case.cross-border-restructure.summary': 'Untangled a 5-year US/Canada filing backlog for a returning expat with zero penalties owed.',

    // testimonials

    // testimonials
    'testimonial.1.role': 'Founder, Northway Studio',
    'testimonial.1.quote': 'WYSE Tribe restructured our corporation and saved us nearly $22k in the first year. They explain everything in plain English \u2013 a rare find in this industry.',
    'testimonial.2.role': 'Self-Employed Consultant',
    'testimonial.2.quote': 'Switched from a big-box tax shop and never looking back. My filings are sharper, my refund was higher, and I actually know what was claimed and why.',
    'testimonial.3.role': 'Real Estate Investor',
    'testimonial.3.quote': 'They handle three corporations and my personal returns. Proactive, responsive, and always one step ahead of the CRA.',
    'testimonial.4.role': 'US/Canada Expat',
    'testimonial.4.quote': 'Cross-border filing used to be a nightmare. WYSE Tribe made it boring \u2013 in the best possible way.',

    // faq
    'faq.1.q': 'How quickly can WYSE Tribe file my personal return?',
    'faq.1.a': 'For straightforward T1 returns we typically file within 3 business days of receiving your documents. Complex returns with self-employment, rental or investment income are completed within 7\u201310 business days.',
    'faq.2.q': 'Do you support both QuickBooks and Xero for bookkeeping?',
    'faq.2.a': 'Yes. Our bookkeeping team is certified on QuickBooks Online and Xero, and we also work with FreshBooks, Wave and custom ERPs when needed.',
    'faq.3.q': 'What happens if the CRA audits me after you file?',
    'faq.3.a': 'We represent you directly. Every WYSE Tribe client receives complimentary CRA correspondence support for any return we prepared, including handling pre-assessment reviews and full audits.',
    'faq.4.q': 'Can you handle cross-border US and Canadian tax filings?',
    'faq.4.a': 'Absolutely. Our cross-border team files US 1040, FBAR, FATCA and Canadian T1/T2 returns side-by-side, applying treaty positions to avoid double taxation.',
    'faq.5.q': 'How is WYSE Tribe different from a big-box tax shop?',
    'faq.5.a': 'We pair year-round advisory with secure cloud workflows. You get a dedicated advisor, transparent pricing and proactive planning \u2013 not a once-a-year transaction.',

    // blog
    'blog.cat.personal': 'Personal',
    'blog.cat.corporate': 'Corporate',
    'blog.cat.audit': 'Audit',
    'blog.2025-tfsa-contribution-room.title': '2025 TFSA Contribution Room \u2013 What\u2019s Changing',
    'blog.2025-tfsa-contribution-room.excerpt': 'The TFSA limit moved again. Here\u2019s what Canadian savers should do this quarter to make the most of the new room.',
    'blog.corporate-class-or-holdco.title': 'Corporate Class Funds or a Holdco? A Founder\u2019s Guide',
    'blog.corporate-class-or-holdco.excerpt': 'Which structure compounds wealth faster after tax \u2013 and which one quietly leaks returns. We break down the math.',
    'blog.cra-audit-defence-2025.title': 'The 7-Step CRA Audit Defence Playbook for 2025',
    'blog.cra-audit-defence-2025.excerpt': 'Audits are up 19% YoY. Use this checklist to bullet-proof your records before any letter ever arrives.',

    // team roles
    'team.role.founder': 'Founder & Lead Tax Advisor, CPA',
    'team.role.corporate': 'Director of Corporate Tax, CPA',
    'team.role.audit': 'Audit & Assurance Lead, CPA',

    'test.eyebrow': 'More than 4,200+ clients',
    'test.titlePre': 'Don\u2019t take our word \u2013',
    'test.titleAccent': 'hear theirs',
    'test.retention': 'Client retention',

    // cases
    'cases.eyebrow': 'Case Studies',
    'cases.titlePre': 'We work passionately to meet your',
    'cases.titleAccent': 'needs',

    // faq
    'faq.eyebrow': 'FAQ',
    'faq.titlePre': 'We always answer',
    'faq.titleAccent': 'doubts',
    'faq.your': 'your',
    'faq.desc':
      'A few of the most common questions from new and existing WYSE Tribe clients. Don\u2019t see yours? Send us a note and we\u2019ll come back the same day.',

    // contact form
    'cf.eyebrow': 'Send a Message',
    'cf.titlePre': 'Request a',
    'cf.titleAccent': 'call back',
    'cf.first': 'First Name',
    'cf.last': 'Last Name',
    'cf.email': 'Email',
    'cf.help': 'How can we help?',
    'cf.message': 'Your message',
    'cf.errFill': 'Please fill in your name, email and message',
    'cf.errCaptcha': 'Please complete the verification challenge',
    'cf.success': 'Thanks! A WYSE Tribe advisor will reach out within 1 business day.',

    // blog
    'blog.eyebrow': 'Our Blog',
    'blog.titlePre': 'Latest blog &',
    'blog.titleAccent': 'articles',
    'blog.desc':
      'Sharp takes on Canadian tax, written by our Tax Specialists. Read time under five minutes \u2013 zero jargon.',

    // page headers
    'page.about': 'About WYSE Tribe',
    'page.services': 'Our Services',
    'page.cases': 'Client Cases',
    'page.blog': 'WYSE Insights',
    'page.contact': 'Contact WYSE Tribe',
    'page.crumb.about': 'About Us',
    'page.crumb.services': 'Services',
    'page.crumb.cases': 'Cases',
    'page.crumb.blog': 'Blog',
    'page.crumb.contact': 'Contact',

    // contact page
    'contact.eyebrow': 'Get in touch',
    'contact.titlePre': 'Book your free',
    'contact.titleAccent': '30-minute strategy call',
    'contact.desc':
      'Tell us a little about your situation and a WYSE Tribe Specialist will reach out within one business day. No sales pitch \u2013 just real answers.',

    // team
    'team.eyebrow': 'Our Team',
    'team.titlePre': 'Meet the people behind',
    'team.titleAccent': 'WYSE Tribe',

    // footer
    'footer.tagline':
      'Canadian tax done WYSE \u2013 personal, corporate and cross-border filings, year-round advisory and CRA-confident representation.',
    'footer.quick': 'Quick Links',
    'footer.useful': 'Useful Links',
    'footer.contact': 'Contact',
    'footer.copy': 'WYSE Tribe \u2013 a brand of WYSETribe.ca. All rights reserved.',
    'footer.cpa': 'CRA Authorized Representative',

    // ---- Member dashboard (AccountPage) ----
    'acct.eyebrow': 'Your account',
    'acct.hi': 'Hi',
    'acct.docVault': 'Document vault',
    'acct.taxFile': 'Tax file',
    'acct.bookNew': 'Book new appointment',
    'acct.signOut': 'Sign out',
    'acct.upcoming': 'Upcoming appointments',
    'acct.loading': 'Loading\u2026',
    'acct.noUpcoming': 'No upcoming appointments yet.',
    'acct.bookFirst': 'Book your first appointment',
    'acct.history': 'History',
    'acct.noPast': 'No past appointments.',
    'acct.mySpecialist': 'My Tax Specialist',
    'acct.noSpec': 'No specialist yet',
    'acct.noSpecDesc': 'Your first booking will assign one.',
    'acct.profile': 'Profile',
    'acct.edit': 'Edit',
    'acct.cancel': 'Cancel',
    'acct.fullName': 'Full name',
    'acct.phonePh': 'Phone',
    'acct.save': 'Save',
    'acct.role': 'Role',
    'acct.helpTitle': 'Need help?',
    'acct.helpBody': 'Reach our team at',
    'acct.offersDesc': 'sent you a personal offer. Review and accept or decline \u2192',
    'acct.offersSpecFallback': 'A specialist',
    'acct.offersWants': 'wants to work with you',
    'acct.offersWantPlural': 'want to work with you',
    'acct.taxSpecialist': 'Tax Specialist',
    'acct.taxSpecialistPlural': 'Tax Specialists',
    'acct.emailNotifsTitle': 'Email notifications',
    'acct.emailNotifsBody': 'Pick which updates land in your inbox.',
    'acct.profileUpdated': 'Profile updated',
    'acct.profileErr': 'Could not update profile',
    'acct.manage': 'Manage',
    'acct.with': 'w/',

    // ---- Login / Signup ----
    'login.back': 'Back to wysetribe.ca',
    'login.eyebrow': 'Client portal',
    'login.heroTitle1': 'Your tax world,',
    'login.heroTitle2': 'always at hand',
    'login.heroDesc': 'Book and manage appointments, share documents securely, and chat with your dedicated WYSE Tribe advisor \u2013 all from one place.',
    'login.feat1': 'Instant booking & rescheduling',
    'login.feat2': 'Year-round advisor access',
    'login.feat3': 'Secure document portal',
    'login.feat4': 'Email reminders before every appointment',
    'login.signinTab': 'Sign in',
    'login.signupTab': 'Sign up',
    'login.welcomeBack': 'Welcome back',
    'login.createAcct': 'Create your account',
    'login.signinSub': 'Sign in to manage your appointments.',
    'login.signupSub': 'Sign up to book and track appointments with WYSE Tribe.',
    'login.invited': 'You\u2019re invited',
    'login.invitedBy': 'by',
    'login.invitedLock': 'Email is locked to your invitation.',
    'login.phonePh': 'Phone (optional)',
    'login.passwordPh': 'Password',
    'login.passwordCreatePh': 'Create a password (min 12 chars)',
    'login.emailPh': 'Email',
    'login.remember': 'Remember me',
    'login.forgot': 'Forgot password?',
    'login.signinBtn': 'Sign in',
    'login.createBtn': 'Create account',
    'login.newHere': 'New here?',
    'login.haveAcct': 'Already have an account?',
    'login.createOne': 'Create an account',
    'login.welcomeToast': 'Welcome back,',
    'login.acctCreated': 'Account created!',
    'login.errLogin': 'Login failed',
    'login.errSignup': 'Sign-up failed',
    'login.showPw': 'Show password',
    'login.hidePw': 'Hide password',
    'login.inviteErr1': 'That invitation is not for member sign-up.',
    'login.inviteErr2': 'This invitation has expired. Please ask for a new one.',
    'login.inviteErr3': 'This invitation has already been used.',
    'login.inviteErr4': 'This invitation has been revoked.',
    'login.inviteErr5': 'Invitation link is invalid.',

    // ---- Forgot / Verify / Reset password ----
    'fp.back': 'Back to sign in',
    'fp.eyebrow': 'Account security',
    'fp.title': 'Forgot your password?',
    'fp.desc': 'Enter your email and we\u2019ll send you a 6-digit verification code to reset it.',
    'fp.emailPh': 'Email address',
    'fp.send': 'Send verification code',
    'fp.checkInbox': 'Check your inbox',
    'fp.footer': 'For your security, we will only send a code if your email matches a registered account. We never reveal whether an account exists.',
    'fp.errMissing': 'Please enter your email',
    'fp.errSend': 'Could not send. Try again.',
    'vc.back': 'Change email',
    'vc.eyebrow': 'Verify your identity',
    'vc.title': 'Enter verification code',
    'vc.desc1': 'We sent a 6-digit code to',
    'vc.desc2': 'The code expires in 15 minutes.',
    'vc.verify': 'Verify and continue',
    'vc.resendIn': 'Resend code in',
    'vc.resend': 'Resend code',
    'vc.enterCode': 'Enter the 6-digit code',
    'vc.verifyFailed': 'Verification failed',
    'vc.resendOk': 'A new code is on its way.',
    'vc.resendErr': 'Could not resend',
    'rp.back': 'Start over',
    'rp.eyebrow': 'Final step',
    'rp.title': 'Set a new password',
    'rp.desc': 'Make it strong \u2014 at least 12 characters with upper + lower case, a number, and a symbol.',
    'rp.newPw': 'New password',
    'rp.confirmPw': 'Confirm new password',
    'rp.strength': 'Strength',
    'rp.sig0': 'Too weak',
    'rp.sig1': 'Weak',
    'rp.sig2': 'Okay',
    'rp.sig3': 'Good',
    'rp.sig4': 'Strong',
    'rp.sig5': 'Very strong',
    'rp.req1': '12+ characters',
    'rp.req2': 'Uppercase letter',
    'rp.req3': 'Lowercase letter',
    'rp.req4': 'Number',
    'rp.req5': 'Special character',
    'rp.req6': 'Passwords match',
    'rp.revoke': 'Sign me out from all other devices',
    'rp.submit': 'Update password',
    'rp.errPolicy': 'Password does not meet the requirements',
    'rp.errMatch': 'Passwords do not match',
    'rp.successToast': 'Password updated.',
    'rp.successTitle': 'Password updated',
    'rp.successBody': 'Your password was changed successfully. We\u2019re signing you in\u2026',
    'rp.errReset': 'Could not reset password',

    // ---- Tax Filing Progress Stepper (member tracker) ----
    'tracker.eyebrow': 'Tax Filing Progress',
    'tracker.taxYear': 'Tax year',
    'tracker.step': 'Step',
    'tracker.of': 'of',
    'tracker.gettingStarted': 'Getting started',
    'tracker.specialistPrefix': 'Your specialist:',
    'tracker.whereYouAre': 'Where you are',
    'tracker.currently': 'Currently:',
    'tracker.specialistNotes': 'Specialist notes',
    'tracker.completed': 'Completed',
    'tracker.actionPill': 'Action',
    'tracker.inProgress': 'In progress',
    'tracker.uploadDocs': 'Upload documents',
    'tracker.reviewSign': 'Review & sign',
    'tracker.viewTaxFile': 'View full tax file',
    'tracker.noFile': 'No tax filing started yet.',
    // Phase labels (6)
    'tracker.p.intake': 'Client Intake Started',
    'tracker.p.intake.desc': 'We have your intake \u2014 your file is officially open.',
    'tracker.p.documents': 'Documents Received',
    'tracker.p.documents.desc': 'We are collecting (and have received) the documents needed for your return.',
    'tracker.p.review': 'Review Complete',
    'tracker.p.review.desc': 'Your specialist is reviewing your documents and preparing your return.',
    'tracker.p.signature': 'Signature Requested',
    'tracker.p.signature.desc': 'Please review and sign your return so we can submit it.',
    'tracker.p.filed_ready': 'Filed Ready',
    'tracker.p.filed_ready.desc': 'Your signed return is ready to be transmitted to the CRA.',
    'tracker.p.cra': 'CRA Submitted',
    'tracker.p.cra.desc': 'Your return has been transmitted to the Canada Revenue Agency.',
  },
  es: {
    // top bar
    'top.home': 'Inicio',
    'top.wysetribe': 'WYSE Tribe',
    'top.locations': 'Calgary · Toronto',
    'top.signIn': 'Iniciar sesión',
    'top.dashboard': 'Mi panel',
    'top.signOut': 'Cerrar sesión',
    'top.language': 'Idioma',

    // header
    'brand.sub': 'Contabilidad · Asesoría Fiscal',
    'nav.home': 'Inicio',
    'nav.about': 'Nosotros',
    'nav.services': 'Servicios',
    'nav.specialists': 'Especialistas',
    // Phase 3 — Specialist directory + profile (Español)
    'spec.eyebrow': 'Nuestros Especialistas Fiscales',
    'spec.heroTitle': 'Conoce a quienes manejarán tu',
    'spec.heroAccent': 'temporada fiscal',
    'spec.heroSubtitle': 'Explora nuestro equipo bilingüe de Especialistas Fiscales con experiencia ante la CRA. Filtra por idioma y reserva directamente con quien mejor te quede.',
    'spec.searchPlaceholder': 'Busca por nombre, cargo o bio…',
    'spec.languageLabel': 'Idioma',
    'spec.langAny': 'Cualquiera',
    'spec.empty': 'Aún no hay especialistas que coincidan con esos filtros.',
    'spec.viewProfile': 'Ver perfil',
    'spec.backToDirectory': 'Volver a todos los especialistas',
    'spec.titleFallback': 'Especialista Fiscal',
    'spec.bookWith': 'Reservar con',
    'spec.about': 'Acerca de',
    'spec.certifications': 'Certificaciones y membresías',
    'spec.services': 'Servicios que ofrecen',
    'spec.workingHours': 'Horario de atención',
    'spec.workingHoursDesc': 'Lun–Vie 9 AM – 5 PM',
    'spec.workingHoursHint': 'Reserva en línea para ver la disponibilidad en vivo.',
    'spec.preferTitle': '¿Prefieres este especialista?',
    'spec.preferDesc': 'Puedes marcarlo como tu especialista preferido desde tu cuenta de miembro.',
    'spec.managePrefs': 'Gestionar preferencias',
    'spec.notFound': 'Especialista no encontrado',
    'spec.notFoundDesc': 'Este perfil pudo haber sido movido o desactivado.',
    'nav.cases': 'Casos',
    'nav.blog': 'Blog',
    'nav.contact': 'Contacto',
    'nav.pages': 'Páginas',
    'cta.bookAppointment': 'Reservar cita',
    'cta.getStarted': 'Comenzar',
    'cta.callUs': 'Llámenos ahora',
    'cta.contactUs': 'Contáctanos',
    'cta.allServices': 'Todos los servicios',
    'cta.allArticles': 'Todos los artículos',
    'cta.learnMore': 'Saber más',
    'cta.talkToAdvisor': 'Hablar con un asesor',
    'cta.readArticle': 'Leer artículo',
    'cta.submitNow': 'Enviar ahora',
    'cta.submitted': 'Enviado',
    'cta.watchStory': 'Mira nuestra historia',

    // hero
    'hero.eyebrow': 'Bienvenido a WYSE Tribe',
    'hero.titlePre': 'Los impuestos son',
    'hero.titleBreak': 'más fáciles de lo que',
    'hero.titleAccent': 'crees',
    'hero.desc':
      'Impuestos personales, corporativos y transfronterizos gestionados por especialistas fiscales que realmente contestan el teléfono. WYSE Tribe combina asesoría durante todo el año con declaraciones de impuestos 100% online, para que conserves más de lo que ganas.',
    'hero.trusted': 'Confianza de más de 4,200 contribuyentes canadienses',

    // highlights
    'hl.expert.title': 'Asesores expertos',
    'hl.expert.text': 'Especialistas fiscales con más de 18 años de experiencia en impuestos canadienses.',
    'hl.consulting.title': 'Consultoría fiscal',
    'hl.consulting.text': 'Planificación fiscal personal, corporativa y transfronteriza que maximiza tus retornos.',
    'hl.planning.title': 'Planificación financiera',
    'hl.planning.text': 'Estrategias patrimoniales integrales según tus metas y etapa de vida.',

    // about
    'about.eyebrow': 'Somos asesores fiscales',
    'about.titlePre': 'Brindamos el mejor servicio para tu',
    'about.titleAccent': 'comodidad',
    'about.desc':
      'WYSE Tribe nació de una idea simple: los impuestos deben ser una herramienta para ayudarte a conservar más de lo que ganas, no una carrera contrarreloj una vez al año. Nuestros especialistas fiscales combinan un profundo conocimiento de la normativa tributaria canadiense (CRA) con un enfoque 100% digital, para que cada cliente reciba respuestas claras, planificación estratégica y cero sorpresas.',
    'about.advisors': 'Más de 32 asesores certificados',
    'about.cpaLine': 'Autorizado por la CRA',
    'about.b.cra': 'Especialistas autorizados por CRA',
    'about.b.pricing': 'Precios fijos y transparentes',
    'about.b.year': 'Soporte fiscal todo el año',
    'about.b.cross': 'Experiencia transfronteriza',
    'about.b.audit': 'Representación en auditorías',
    'about.b.secure': 'Confidencial y seguro',
    'about.founder': 'Fundador · CPA',

    // stats
    'stats.clients': 'Clientes felices',
    'stats.returns': 'Declaraciones presentadas',
    'stats.years': 'Años de experiencia',
    'stats.experts': 'Expertos fiscales',

    // comfort
    'comfort.titlePre': 'Nuestro plan te hace sentir',
    'comfort.titleAccent': 'más cómodo',
    'comfort.titlePost': 'en la gestión fiscal',
    'comfort.desc':
      'Desde la primera llamada hasta tu revisión posterior a la presentación, cada paso está planificado. Siempre sabrás qué ocurre con tus impuestos — y qué viene a continuación.',
    'comfort.f1.t': 'Especialista fiscal dedicado',
    'comfort.f1.d': 'Un asesor que conoce tu expediente de principio a fin.',
    'comfort.f2.t': 'Soporte todo el año',
    'comfort.f2.d': 'Revisiones trimestrales, no solo en temporada fiscal.',
    'comfort.f3.t': 'Representación ante la CRA',
    'comfort.f3.d': 'Atendemos cada carta, revisión y auditoría.',

    // services
    'serv.eyebrow': 'Servicios',
    'serv.titlePre': 'Nuestros servicios',
    'serv.titleAccent': 'incluyen',
    'serv.desc':
      'Declaraciones personales, presentaciones corporativas, defensa en auditorías y trabajo transfronterizo — todo bajo un mismo techo, todo el año.',
    'serv.pageEyebrow': 'Lo que hacemos',
    'serv.pageTitlePre': 'Cada servicio que tu vida fiscal',
    'serv.pageTitleAccent': 'necesita',
    'serv.pageDesc':
      'Desde un simple T1 hasta estructuras multi-entidad y multi-país — WYSE Tribe los gestiona con el mismo cuidado.',

    // service cards (per service id)
    'service.personal-tax.title': 'Impuestos personales',
    'service.personal-tax.short': 'Maximiza tu reembolso con declaraciones personales precisas y conformes a CRA.',
    'service.personal-tax.desc': 'Desde empleados con T4 hasta contratistas independientes, manejamos toda declaración personal canadiense con cuidado. Identificamos cada deducción y crédito elegible para que conserves más de lo que ganas.',
    'service.corporate-tax.title': 'Impuestos corporativos',
    'service.corporate-tax.short': 'Declaraciones T2, planificación de cierre de año y estructuras fiscalmente eficientes.',
    'service.corporate-tax.desc': 'Planificación estratégica de impuestos corporativos, declaraciones T2, presentaciones de GST/HST y correspondencia con CRA gestionadas de principio a fin para que tu negocio se enfoque en crecer.',
    'service.audit-assurance.title': 'Auditoría y aseguramiento',
    'service.audit-assurance.short': 'Representación confiable en auditorías CRA y estados financieros revisados.',
    'service.audit-assurance.desc': 'Duerme tranquilo sabiendo que nuestros especialistas en auditoría te representan directamente ante CRA. Preparamos la documentación, negociamos resultados y protegemos tu posición.',
    'service.bookkeeping.title': 'Contabilidad',
    'service.bookkeeping.short': 'Libros ordenados, reportes mensuales y flujos de trabajo 100% en la nube.',
    'service.bookkeeping.desc': 'Contabilidad mensual con integración a QuickBooks y Xero, nómina, conciliaciones bancarias y reportes gerenciales que realmente entiendes.',
    'service.cross-border.title': 'Impuestos transfronterizos',
    'service.cross-border.short': 'Declaraciones Canadá\u2013EE.UU., FBAR y optimización de tratados.',
    'service.cross-border.desc': 'Declaraciones transfronterizas EE.UU.\u2013Canadá, FBAR, FATCA, posiciones de tratado y planificación para expatriados. Hacemos que las declaraciones dobles sean sencillas y conformes.',
    'service.financial-planning.title': 'Planificación financiera',
    'service.financial-planning.short': 'Estrategias de RRSP, TFSA, retiro y patrimonio que multiplican tu capital.',
    'service.financial-planning.desc': 'Un plan integral que cubre inversiones, retiro, seguros y patrimonio — alineado con tu posición fiscal para que cada dólar trabaje más duro.',

    // case studies
    'case.cat.corporate': 'Corporativo',
    'case.cat.family-office': 'Family Office',
    'case.cat.personal': 'Personal',
    'case.cat.cross-border': 'Transfronterizo',
    'case.short-range-tax-planning.title': 'Planificación fiscal de corto plazo',
    'case.short-range-tax-planning.summary': 'Optimización de cierre de año para una agencia de Toronto, reduciendo el impuesto a pagar en un 28% en un solo año fiscal.',
    'case.purposive-tax-planning.title': 'Planificación fiscal estratégica',
    'case.purposive-tax-planning.summary': 'Estructuramos un fideicomiso familiar y una sociedad holding para un cliente multi-generacional, multiplicando su patrimonio de forma fiscalmente eficiente.',
    'case.permissive-tax-planning.title': 'Planificación fiscal permitida',
    'case.permissive-tax-planning.summary': 'Aprovechamos cada crédito y deducción permitida por CRA para un trabajador independiente, aumentando su reembolso en $14,800.',
    'case.cross-border-restructure.title': 'Reestructuración transfronteriza',
    'case.cross-border-restructure.summary': 'Resolvimos 5 años de declaraciones EE.UU./Canadá pendientes de un expatriado que regresó, sin pagar una sola penalidad.',

    // testimonials
    'testimonial.1.role': 'Fundadora, Northway Studio',
    'testimonial.1.quote': 'WYSE Tribe reestructuró nuestra corporación y nos ahorró casi $22,000 en el primer año. Explican todo de forma sencilla — algo poco común en este sector.',
    'testimonial.2.role': 'Consultor independiente',
    'testimonial.2.quote': 'Cambié de una cadena tradicional de impuestos y no miro atrás. Mis declaraciones son más precisas, mi reembolso fue mayor, y entiendo exactamente qué se reclamó y por qué.',
    'testimonial.3.role': 'Inversionista de bienes raíces',
    'testimonial.3.quote': 'Manejan tres corporaciones y mis declaraciones personales. Proactivos, atentos y siempre un paso adelante de CRA.',
    'testimonial.4.role': 'Expatriado EE.UU./Canadá',
    'testimonial.4.quote': 'Declarar impuestos transfronterizos solía ser una pesadilla. WYSE Tribe lo volvió rutinario — en el mejor sentido.',

    // faq
    'faq.1.q': '¿Qué tan rápido pueden presentar mi declaración personal?',
    'faq.1.a': 'Las declaraciones T1 sencillas las presentamos típicamente en 3 días hábiles desde la recepción de tus documentos. Las declaraciones complejas con ingreso por autoempleo, alquiler o inversiones se completan en 7 a 10 días hábiles.',
    'faq.2.q': '¿Trabajan con QuickBooks y Xero para contabilidad?',
    'faq.2.a': 'Sí. Nuestro equipo está certificado en QuickBooks Online y Xero, y también trabajamos con FreshBooks, Wave y ERPs personalizados cuando se requiere.',
    'faq.3.q': '¿Qué pasa si CRA me audita después de presentar?',
    'faq.3.a': 'Te representamos directamente. Cada cliente de WYSE Tribe recibe soporte gratuito de correspondencia con CRA para cualquier declaración que hayamos preparado, incluyendo revisiones previas y auditorías completas.',
    'faq.4.q': '¿Pueden gestionar declaraciones transfronterizas EE.UU./Canadá?',
    'faq.4.a': 'Por supuesto. Nuestro equipo transfronterizo presenta 1040 de EE.UU., FBAR, FATCA y declaraciones canadienses T1/T2 en paralelo, aplicando posiciones de tratado para evitar la doble tributación.',
    'faq.5.q': '¿En qué se diferencia WYSE Tribe de una cadena de impuestos tradicional?',
    'faq.5.a': 'Combinamos asesoría durante todo el año con flujos seguros en la nube. Tienes un asesor dedicado, precios transparentes y planificación proactiva — no una transacción anual.',

    // blog
    'blog.cat.personal': 'Personal',
    'blog.cat.corporate': 'Corporativo',
    'blog.cat.audit': 'Auditoría',
    'blog.2025-tfsa-contribution-room.title': 'Espacio de aporte TFSA 2025 — qué está cambiando',
    'blog.2025-tfsa-contribution-room.excerpt': 'El límite del TFSA se movió otra vez. Esto es lo que los ahorradores canadienses deberían hacer este trimestre para aprovechar el nuevo espacio.',
    'blog.corporate-class-or-holdco.title': '¿Fondos Corporate Class o un Holdco? Guía para fundadores',
    'blog.corporate-class-or-holdco.excerpt': 'Qué estructura multiplica más rápido tu patrimonio después de impuestos — y cuál lo deja escapar silenciosamente. Analizamos los números.',
    'blog.cra-audit-defence-2025.title': 'Guía de defensa de auditoría CRA en 7 pasos para 2025',
    'blog.cra-audit-defence-2025.excerpt': 'Las auditorías subieron 19% interanual. Usa esta lista para blindar tus registros antes de que llegue cualquier carta.',

    // team roles
    'team.role.founder': 'Fundador y Asesor Fiscal Principal, CPA',
    'team.role.corporate': 'Directora de Impuestos Corporativos, CPA',
    'team.role.audit': 'Líder de Auditoría y Aseguramiento, CPA',


    // testimonials
    'test.eyebrow': 'Más de 4,200 clientes',
    'test.titlePre': 'No nos creas a nosotros —',
    'test.titleAccent': 'escúchalos a ellos',
    'test.retention': 'Retención de clientes',

    // cases
    'cases.eyebrow': 'Estudios de caso',
    'cases.titlePre': 'Trabajamos con pasión para cumplir tus',
    'cases.titleAccent': 'necesidades',

    // faq
    'faq.eyebrow': 'Preguntas frecuentes',
    'faq.titlePre': 'Siempre respondemos',
    'faq.titleAccent': 'dudas',
    'faq.your': 'tus',
    'faq.desc':
      'Algunas de las preguntas más comunes de clientes nuevos y existentes de WYSE Tribe. ¿No ves la tuya? Envíanos un mensaje y te responderemos el mismo día.',

    // contact form
    'cf.eyebrow': 'Envíanos un mensaje',
    'cf.titlePre': 'Solicita una',
    'cf.titleAccent': 'devolución de llamada',
    'cf.first': 'Nombre',
    'cf.last': 'Apellido',
    'cf.email': 'Correo electrónico',
    'cf.help': '¿En qué podemos ayudarte?',
    'cf.message': 'Tu mensaje',
    'cf.errFill': 'Por favor completa nombre, correo y mensaje',
    'cf.errCaptcha': 'Por favor completa la verificación',
    'cf.success': '¡Gracias! Un asesor de WYSE Tribe se pondrá en contacto en 1 día hábil.',

    // blog
    'blog.eyebrow': 'Nuestro blog',
    'blog.titlePre': 'Últimos artículos y',
    'blog.titleAccent': 'novedades',
    'blog.desc':
      'Análisis claros sobre impuestos canadienses, escritos por nuestros especialistas fiscales. Menos de cinco minutos de lectura — cero tecnicismos.',

    // page headers
    'page.about': 'Sobre WYSE Tribe',
    'page.services': 'Nuestros servicios',
    'page.cases': 'Casos de clientes',
    'page.blog': 'WYSE Insights',
    'page.contact': 'Contacta a WYSE Tribe',
    'page.crumb.about': 'Nosotros',
    'page.crumb.services': 'Servicios',
    'page.crumb.cases': 'Casos',
    'page.crumb.blog': 'Blog',
    'page.crumb.contact': 'Contacto',

    // contact page
    'contact.eyebrow': 'Contáctanos',
    'contact.titlePre': 'Reserva tu',
    'contact.titleAccent': 'llamada estratégica de 30 minutos',
    'contact.desc':
      'Cuéntanos un poco sobre tu situación y un especialista de WYSE Tribe se pondrá en contacto en un día hábil. Sin presión de ventas — solo respuestas reales.',

    // team
    'team.eyebrow': 'Nuestro equipo',
    'team.titlePre': 'Conoce a las personas detrás de',
    'team.titleAccent': 'WYSE Tribe',

    // footer
    'footer.tagline':
      'Impuestos canadienses al estilo WYSE — presentaciones personales, corporativas y transfronterizas, asesoría durante todo el año y representación ante la CRA con confianza.',
    'footer.quick': 'Enlaces rápidos',
    'footer.useful': 'Enlaces útiles',
    'footer.contact': 'Contacto',
    'footer.copy': 'WYSE Tribe — una marca de WYSETribe.ca. Todos los derechos reservados.',
    'footer.cpa': 'Representante autorizado por la CRA',

    // ---- Member dashboard (AccountPage) ----
    'acct.eyebrow': 'Tu cuenta',
    'acct.hi': 'Hola',
    'acct.docVault': 'Bóveda de documentos',
    'acct.taxFile': 'Expediente fiscal',
    'acct.bookNew': 'Reservar nueva cita',
    'acct.signOut': 'Cerrar sesión',
    'acct.upcoming': 'Próximas citas',
    'acct.loading': 'Cargando…',
    'acct.noUpcoming': 'Aún no hay próximas citas.',
    'acct.bookFirst': 'Reserva tu primera cita',
    'acct.history': 'Historial',
    'acct.noPast': 'Sin citas anteriores.',
    'acct.mySpecialist': 'Mi especialista fiscal',
    'acct.noSpec': 'Aún sin especialista',
    'acct.noSpecDesc': 'Tu primera reserva asignará uno.',
    'acct.profile': 'Perfil',
    'acct.edit': 'Editar',
    'acct.cancel': 'Cancelar',
    'acct.fullName': 'Nombre completo',
    'acct.phonePh': 'Teléfono',
    'acct.save': 'Guardar',
    'acct.role': 'Rol',
    'acct.helpTitle': '¿Necesitas ayuda?',
    'acct.helpBody': 'Contacta a nuestro equipo en',
    'acct.offersDesc': 'te envió una oferta personal. Revísala y acéptala o decline →',
    'acct.offersSpecFallback': 'Un especialista',
    'acct.offersWants': 'quiere trabajar contigo',
    'acct.offersWantPlural': 'quieren trabajar contigo',
    'acct.taxSpecialist': 'Especialista fiscal',
    'acct.taxSpecialistPlural': 'Especialistas fiscales',
    'acct.emailNotifsTitle': 'Notificaciones por correo',
    'acct.emailNotifsBody': 'Elige qué actualizaciones llegan a tu bandeja.',
    'acct.profileUpdated': 'Perfil actualizado',
    'acct.profileErr': 'No se pudo actualizar el perfil',
    'acct.manage': 'Gestionar',
    'acct.with': 'con',

    // ---- Login / Signup ----
    'login.back': 'Volver a wysetribe.ca',
    'login.eyebrow': 'Portal de cliente',
    'login.heroTitle1': 'Tu mundo fiscal,',
    'login.heroTitle2': 'siempre a la mano',
    'login.heroDesc': 'Reserva y gestiona tus citas, comparte documentos de forma segura y comunícate con tu asesor dedicado de WYSE Tribe — todo en un solo lugar.',
    'login.feat1': 'Reservas y reprogramaciones al instante',
    'login.feat2': 'Acceso al asesor todo el año',
    'login.feat3': 'Portal seguro de documentos',
    'login.feat4': 'Recordatorios por correo antes de cada cita',
    'login.signinTab': 'Iniciar sesión',
    'login.signupTab': 'Registrarse',
    'login.welcomeBack': 'Bienvenido de vuelta',
    'login.createAcct': 'Crea tu cuenta',
    'login.signinSub': 'Inicia sesión para gestionar tus citas.',
    'login.signupSub': 'Regístrate para reservar y dar seguimiento a tus citas con WYSE Tribe.',
    'login.invited': 'Te invitaron',
    'login.invitedBy': 'por',
    'login.invitedLock': 'El correo está vinculado a tu invitación.',
    'login.phonePh': 'Teléfono (opcional)',
    'login.passwordPh': 'Contraseña',
    'login.passwordCreatePh': 'Crea una contraseña (mín 12 caracteres)',
    'login.emailPh': 'Correo electrónico',
    'login.remember': 'Recordarme',
    'login.forgot': '¿Olvidaste tu contraseña?',
    'login.signinBtn': 'Iniciar sesión',
    'login.createBtn': 'Crear cuenta',
    'login.newHere': '¿Nuevo aquí?',
    'login.haveAcct': '¿Ya tienes cuenta?',
    'login.createOne': 'Crear una cuenta',
    'login.welcomeToast': 'Bienvenido de vuelta,',
    'login.acctCreated': '¡Cuenta creada!',
    'login.errLogin': 'Inicio de sesión fallido',
    'login.errSignup': 'Registro fallido',
    'login.showPw': 'Mostrar contraseña',
    'login.hidePw': 'Ocultar contraseña',
    'login.inviteErr1': 'Esa invitación no es para registro de miembros.',
    'login.inviteErr2': 'Esta invitación caducó. Solicita una nueva.',
    'login.inviteErr3': 'Esta invitación ya fue utilizada.',
    'login.inviteErr4': 'Esta invitación fue revocada.',
    'login.inviteErr5': 'El enlace de invitación es inválido.',

    // ---- Forgot / Verify / Reset password ----
    'fp.back': 'Volver a iniciar sesión',
    'fp.eyebrow': 'Seguridad de la cuenta',
    'fp.title': '¿Olvidaste tu contraseña?',
    'fp.desc': 'Ingresa tu correo y te enviaremos un código de verificación de 6 dígitos para restablecerla.',
    'fp.emailPh': 'Correo electrónico',
    'fp.send': 'Enviar código de verificación',
    'fp.checkInbox': 'Revisa tu correo',
    'fp.footer': 'Por tu seguridad, solo enviamos el código si tu correo coincide con una cuenta registrada. Nunca revelamos si una cuenta existe.',
    'fp.errMissing': 'Por favor ingresa tu correo',
    'fp.errSend': 'No se pudo enviar. Inténtalo de nuevo.',
    'vc.back': 'Cambiar correo',
    'vc.eyebrow': 'Verifica tu identidad',
    'vc.title': 'Ingresa el código de verificación',
    'vc.desc1': 'Enviamos un código de 6 dígitos a',
    'vc.desc2': 'El código expira en 15 minutos.',
    'vc.verify': 'Verificar y continuar',
    'vc.resendIn': 'Reenviar código en',
    'vc.resend': 'Reenviar código',
    'vc.enterCode': 'Ingresa el código de 6 dígitos',
    'vc.verifyFailed': 'Verificación fallida',
    'vc.resendOk': 'Un nuevo código está en camino.',
    'vc.resendErr': 'No se pudo reenviar',
    'rp.back': 'Empezar de nuevo',
    'rp.eyebrow': 'Paso final',
    'rp.title': 'Define una nueva contraseña',
    'rp.desc': 'Hazla fuerte: al menos 12 caracteres con mayúsculas, minúsculas, un número y un símbolo.',
    'rp.newPw': 'Nueva contraseña',
    'rp.confirmPw': 'Confirma la nueva contraseña',
    'rp.strength': 'Fortaleza',
    'rp.sig0': 'Demasiado débil',
    'rp.sig1': 'Débil',
    'rp.sig2': 'Aceptable',
    'rp.sig3': 'Buena',
    'rp.sig4': 'Fuerte',
    'rp.sig5': 'Muy fuerte',
    'rp.req1': '12+ caracteres',
    'rp.req2': 'Mayúscula',
    'rp.req3': 'Minúscula',
    'rp.req4': 'Número',
    'rp.req5': 'Carácter especial',
    'rp.req6': 'Las contraseñas coinciden',
    'rp.revoke': 'Cerrar sesión en todos los demás dispositivos',
    'rp.submit': 'Actualizar contraseña',
    'rp.errPolicy': 'La contraseña no cumple los requisitos',
    'rp.errMatch': 'Las contraseñas no coinciden',
    'rp.successToast': 'Contraseña actualizada.',
    'rp.successTitle': 'Contraseña actualizada',
    'rp.successBody': 'Tu contraseña se cambió con éxito. Iniciando sesión…',
    'rp.errReset': 'No se pudo restablecer la contraseña',

    // ---- Tax Filing Progress Stepper (member tracker) ----
    'tracker.eyebrow': 'Progreso de tu declaración',
    'tracker.taxYear': 'Año fiscal',
    'tracker.step': 'Paso',
    'tracker.of': 'de',
    'tracker.gettingStarted': 'Comenzando',
    'tracker.specialistPrefix': 'Tu especialista:',
    'tracker.whereYouAre': 'En qué etapa estás',
    'tracker.currently': 'Actualmente:',
    'tracker.specialistNotes': 'Notas del especialista',
    'tracker.completed': 'Completado',
    'tracker.actionPill': 'Acción',
    'tracker.inProgress': 'En progreso',
    'tracker.uploadDocs': 'Subir documentos',
    'tracker.reviewSign': 'Revisar y firmar',
    'tracker.viewTaxFile': 'Ver expediente completo',
    'tracker.noFile': 'Aún no se ha iniciado ninguna declaración.',
    'tracker.p.intake': 'Inicio del expediente',
    'tracker.p.intake.desc': 'Recibimos tu información de ingreso — tu expediente está abierto oficialmente.',
    'tracker.p.documents': 'Documentos recibidos',
    'tracker.p.documents.desc': 'Estamos recopilando (y hemos recibido) los documentos necesarios para tu declaración.',
    'tracker.p.review': 'Revisión completa',
    'tracker.p.review.desc': 'Tu especialista está revisando tus documentos y preparando tu declaración.',
    'tracker.p.signature': 'Firma solicitada',
    'tracker.p.signature.desc': 'Por favor revisa y firma tu declaración para que podamos enviarla.',
    'tracker.p.filed_ready': 'Lista para presentar',
    'tracker.p.filed_ready.desc': 'Tu declaración firmada está lista para ser transmitida a la CRA.',
    'tracker.p.cra': 'Enviada a la CRA',
    'tracker.p.cra.desc': 'Tu declaración fue transmitida a la Canada Revenue Agency.',
  },
  fr: {
    // top bar
    'top.home': 'Accueil',
    'top.wysetribe': 'WYSE Tribe',
    'top.locations': 'Calgary · Toronto',
    'top.signIn': 'Connexion',
    'top.dashboard': 'Mon tableau de bord',
    'top.signOut': 'Déconnexion',
    'top.language': 'Langue',

    // header
    'brand.sub': 'Comptabilité · Conseil fiscal',
    'nav.home': 'Accueil',
    'nav.about': 'À propos',
    'nav.services': 'Services',
    'nav.specialists': 'Spécialistes',
    // Phase 3 — Specialist directory + profile (Français)
    'spec.eyebrow': 'Nos spécialistes fiscaux',
    'spec.heroTitle': 'Rencontrez les personnes qui géreront votre',
    'spec.heroAccent': 'saison des impôts',
    'spec.heroSubtitle': 'Parcourez notre équipe trilingue de spécialistes fiscaux reconnus par l’ARC. Filtrez par la langue dans laquelle vous souhaitez travailler, puis réservez directement avec la personne qui vous convient.',
    'spec.searchPlaceholder': 'Rechercher par nom, titre ou bio…',
    'spec.languageLabel': 'Langue',
    'spec.langAny': 'Toutes',
    'spec.empty': 'Aucun spécialiste ne correspond à ces filtres pour le moment.',
    'spec.viewProfile': 'Voir le profil',
    'spec.backToDirectory': 'Retour à tous les spécialistes',
    'spec.titleFallback': 'Spécialiste fiscal',
    'spec.bookWith': 'Réserver avec',
    'spec.about': 'À propos',
    'spec.certifications': 'Certifications et adhésions',
    'spec.services': 'Services offerts',
    'spec.workingHours': 'Heures d’ouverture',
    'spec.workingHoursDesc': 'Lun–Ven 9 h – 17 h',
    'spec.workingHoursHint': 'Réservez en ligne pour voir les disponibilités en direct.',
    'spec.preferTitle': 'Vous préférez ce spécialiste ?',
    'spec.preferDesc': 'Vous pouvez le désigner comme votre spécialiste préféré depuis votre compte membre.',
    'spec.managePrefs': 'Gérer mes préférences',
    'spec.notFound': 'Spécialiste introuvable',
    'spec.notFoundDesc': 'Ce profil a peut-être été déplacé ou désactivé.',
    'nav.cases': 'Études de cas',
    'nav.blog': 'Blogue',
    'nav.contact': 'Contact',
    'nav.pages': 'Pages',
    'cta.bookAppointment': 'Prendre rendez-vous',
    'cta.getStarted': 'Commencer',
    'cta.callUs': 'Appelez-nous',
    'cta.contactUs': 'Nous joindre',
    'cta.allServices': 'Tous les services',
    'cta.allArticles': 'Tous les articles',
    'cta.learnMore': 'En savoir plus',
    'cta.talkToAdvisor': 'Parler à un conseiller',
    'cta.readArticle': 'Lire l’article',
    'cta.submitNow': 'Envoyer maintenant',
    'cta.submitted': 'Envoyé',
    'cta.watchStory': 'Regarder notre histoire',

    // hero
    'hero.eyebrow': 'Bienvenue chez WYSE Tribe',
    'hero.titlePre': 'Les impôts sont plus',
    'hero.titleBreak': 'faciles que vous ne le',
    'hero.titleAccent': 'pensez',
    'hero.desc':
      'Impôts personnels, corporatifs et transfrontaliers gérés par des spécialistes fiscaux qui répondent vraiment au téléphone. WYSE Tribe combine conseil tout au long de l’année et production 100 % en ligne — pour que vous conserviez davantage de ce que vous gagnez.',
    'hero.trusted': 'La confiance de plus de 4 200 contribuables canadiens',

    // highlights
    'hl.expert.title': 'Conseillers experts',
    'hl.expert.text': 'Spécialistes fiscaux avec plus de 18 ans d’expérience en fiscalité canadienne.',
    'hl.consulting.title': 'Conseil fiscal',
    'hl.consulting.text': 'Planification fiscale personnelle, corporative et transfrontalière qui maximise vos retours.',
    'hl.planning.title': 'Planification financière',
    'hl.planning.text': 'Stratégies patrimoniales globales adaptées à vos objectifs et à votre étape de vie.',

    // about
    'about.eyebrow': 'Nous sommes des conseillers fiscaux',
    'about.titlePre': 'Nous offrons le meilleur service pour votre',
    'about.titleAccent': 'tranquillité',
    'about.desc':
      'WYSE Tribe est née d’une idée simple : l’impôt devrait être un levier qui fait croître votre patrimoine, pas une course contre la montre une fois par an. Nos spécialistes fiscaux allient une solide expertise de l’ARC à un flux 100 % numérique pour que chaque client obtienne des réponses claires, une vraie planification et zéro surprise.',
    'about.advisors': 'Plus de 32 conseillers certifiés',
    'about.cpaLine': 'Autorisé par l’ARC',
    'about.b.cra': 'Spécialistes autorisés par l’ARC',
    'about.b.pricing': 'Tarification fixe et transparente',
    'about.b.year': 'Soutien fiscal toute l’année',
    'about.b.cross': 'Expertise transfrontalière',
    'about.b.audit': 'Représentation en cas de vérification',
    'about.b.secure': 'Confidentiel et sécurisé',
    'about.founder': 'Fondateur · CPA',

    // stats
    'stats.clients': 'Clients satisfaits',
    'stats.returns': 'Déclarations produites',
    'stats.years': 'Années d’expérience',
    'stats.experts': 'Experts fiscaux',

    // comfort
    'comfort.titlePre': 'Notre approche vous met',
    'comfort.titleAccent': 'plus à l’aise',
    'comfort.titlePost': 'face à la gestion fiscale',
    'comfort.desc':
      'Du premier appel d’accueil à la révision après production, chaque étape est planifiée. Vous saurez toujours où en est votre dossier — et ce qui suit.',
    'comfort.f1.t': 'Spécialiste fiscal dédié',
    'comfort.f1.d': 'Un conseiller qui connaît votre dossier sur le bout des doigts.',
    'comfort.f2.t': 'Soutien toute l’année',
    'comfort.f2.d': 'Suivis trimestriels, pas uniquement en saison fiscale.',
    'comfort.f3.t': 'Représentation auprès de l’ARC',
    'comfort.f3.d': 'Nous gérons chaque lettre, révision et vérification.',

    // services
    'serv.eyebrow': 'Services',
    'serv.titlePre': 'Nos services',
    'serv.titleAccent': 'incluent',
    'serv.desc':
      'Déclarations personnelles, productions corporatives, défense en cas de vérification et fiscalité transfrontalière — le tout sous un même toit, toute l’année.',
    'serv.pageEyebrow': 'Ce que nous faisons',
    'serv.pageTitlePre': 'Tous les services dont votre vie fiscale',
    'serv.pageTitleAccent': 'a besoin',
    'serv.pageDesc':
      'Du simple T1 aux structures multi-entités et multi-pays — WYSE Tribe les gère avec le même soin.',

    // service cards (per service id)
    'service.personal-tax.title': 'Impôts des particuliers',
    'service.personal-tax.short': 'Maximisez votre remboursement avec des déclarations personnelles conformes à l’ARC.',
    'service.personal-tax.desc': 'Des salariés avec T4 aux travailleurs autonomes, nous traitons chaque déclaration personnelle canadienne avec soin. Nous repérons chaque déduction et crédit admissible pour que vous gardiez davantage de ce que vous gagnez.',
    'service.corporate-tax.title': 'Impôts corporatifs',
    'service.corporate-tax.short': 'Déclarations T2, planification de fin d’année et structures fiscalement efficaces.',
    'service.corporate-tax.desc': 'Planification stratégique des impôts corporatifs, déclarations T2, productions TPS/TVH et correspondance avec l’ARC gérées de bout en bout pour que votre entreprise se concentre sur sa croissance.',
    'service.audit-assurance.title': 'Audit et certification',
    'service.audit-assurance.short': 'Représentation fiable lors de vérifications de l’ARC et états financiers révisés.',
    'service.audit-assurance.desc': 'Dormez tranquille : nos spécialistes d’audit vous représentent directement auprès de l’ARC. Nous préparons la documentation, négocions les résultats et protégeons votre position.',
    'service.bookkeeping.title': 'Tenue de livres',
    'service.bookkeeping.short': 'Livres impeccables, rapports mensuels et flux 100 % infonuagiques.',
    'service.bookkeeping.desc': 'Tenue de livres mensuelle avec intégration QuickBooks et Xero, paie, conciliations bancaires et rapports de gestion compréhensibles.',
    'service.cross-border.title': 'Fiscalité transfrontalière',
    'service.cross-border.short': 'Déclarations Canada\u2013É.-U., FBAR et optimisation des conventions fiscales.',
    'service.cross-border.desc': 'Productions transfrontalières É.-U.\u2013Canada, FBAR, FATCA, positions de convention et planification pour expatriés. Nous rendons les doubles déclarations simples et conformes.',
    'service.financial-planning.title': 'Planification financière',
    'service.financial-planning.short': 'Stratégies REER, CELI, retraite et patrimoine qui font croître votre capital.',
    'service.financial-planning.desc': 'Un plan complet couvrant placements, retraite, assurance et patrimoine — aligné avec votre situation fiscale pour que chaque dollar travaille plus fort.',

    // case studies
    'case.cat.corporate': 'Corporatif',
    'case.cat.family-office': 'Family Office',
    'case.cat.personal': 'Personnel',
    'case.cat.cross-border': 'Transfrontalier',
    'case.short-range-tax-planning.title': 'Planification fiscale à court terme',
    'case.short-range-tax-planning.summary': 'Optimisation de fin d’année pour une agence torontoise, réduisant l’impôt à payer de 28 % en une seule année fiscale.',
    'case.purposive-tax-planning.title': 'Planification fiscale stratégique',
    'case.purposive-tax-planning.summary': 'Mise en place d’une fiducie familiale et d’une société de portefeuille pour un client multigénérationnel afin de faire croître le patrimoine de façon fiscalement efficace.',
    'case.permissive-tax-planning.title': 'Planification fiscale permise',
    'case.permissive-tax-planning.summary': 'Mobilisation de chaque crédit et déduction permis par l’ARC pour un travailleur autonome, augmentant son remboursement de 14 800 $.',
    'case.cross-border-restructure.title': 'Restructuration transfrontalière',
    'case.cross-border-restructure.summary': 'Régularisation d’un retard de 5 ans en déclarations É.-U./Canada pour un expatrié de retour, sans aucune pénalité.',

    // testimonials
    'testimonial.1.role': 'Fondatrice, Northway Studio',
    'testimonial.1.quote': 'WYSE Tribe a restructuré notre société et nous a fait économiser près de 22 000 $ la première année. Tout est expliqué simplement — une rareté dans le secteur.',
    'testimonial.2.role': 'Consultant autonome',
    'testimonial.2.quote': 'J’ai quitté un grand centre fiscal et je ne reviendrai pas en arrière. Mes déclarations sont plus précises, mon remboursement plus élevé, et je sais exactement ce qui a été réclamé.',
    'testimonial.3.role': 'Investisseur immobilier',
    'testimonial.3.quote': 'Ils gèrent trois sociétés et mes déclarations personnelles. Proactifs, réactifs et toujours en avance sur l’ARC.',
    'testimonial.4.role': 'Expatrié É.-U./Canada',
    'testimonial.4.quote': 'Les déclarations transfrontalières étaient un cauchemar. WYSE Tribe a rendu ça… ennuyeusement simple — dans le meilleur sens.',

    // faq
    'faq.1.q': 'En combien de temps WYSE Tribe peut-elle produire ma déclaration personnelle ?',
    'faq.1.a': 'Pour une T1 simple, nous produisons généralement dans les 3 jours ouvrables suivant la réception de vos documents. Les déclarations complexes (travailleur autonome, location, placements) sont complétées en 7 à 10 jours ouvrables.',
    'faq.2.q': 'Supportez-vous QuickBooks et Xero pour la tenue de livres ?',
    'faq.2.a': 'Oui. Notre équipe est certifiée QuickBooks en ligne et Xero, et nous travaillons aussi avec FreshBooks, Wave et des ERP sur mesure au besoin.',
    'faq.3.q': 'Que se passe-t-il si l’ARC me vérifie après la production ?',
    'faq.3.a': 'Nous vous représentons directement. Chaque client WYSE Tribe bénéficie d’un soutien gratuit pour toute correspondance avec l’ARC concernant une déclaration que nous avons préparée — incluant les révisions de pré-cotisation et les vérifications complètes.',
    'faq.4.q': 'Pouvez-vous gérer des productions transfrontalières É.-U./Canada ?',
    'faq.4.a': 'Tout à fait. Notre équipe transfrontalière produit en parallèle les 1040 américains, FBAR, FATCA et les déclarations canadiennes T1/T2, en appliquant les positions de convention pour éviter la double imposition.',
    'faq.5.q': 'En quoi WYSE Tribe se distingue-t-elle d’un grand centre fiscal ?',
    'faq.5.a': 'Nous combinons conseil tout au long de l’année et flux infonuagiques sécurisés. Vous avez un conseiller dédié, des tarifs transparents et une planification proactive — pas une transaction annuelle.',

    // blog
    'blog.cat.personal': 'Personnel',
    'blog.cat.corporate': 'Corporatif',
    'blog.cat.audit': 'Vérification',
    'blog.2025-tfsa-contribution-room.title': 'Plafond CELI 2025 — ce qui change',
    'blog.2025-tfsa-contribution-room.excerpt': 'Le plafond du CELI a encore bougé. Voici ce que les épargnants canadiens devraient faire ce trimestre pour profiter du nouvel espace.',
    'blog.corporate-class-or-holdco.title': 'Fonds catégorie de société ou Holdco ? Le guide du fondateur',
    'blog.corporate-class-or-holdco.excerpt': 'Quelle structure fait croître le patrimoine plus vite après impôt — et laquelle laisse silencieusement filer du rendement. Les chiffres, expliqués.',
    'blog.cra-audit-defence-2025.title': 'Le plan de défense en 7 étapes face à une vérification ARC pour 2025',
    'blog.cra-audit-defence-2025.excerpt': 'Les vérifications ont augmenté de 19 % sur un an. Utilisez cette liste pour blinder vos dossiers avant même qu’une lettre n’arrive.',

    // team roles
    'team.role.founder': 'Fondateur et conseiller fiscal principal, CPA',
    'team.role.corporate': 'Directrice de la fiscalité corporative, CPA',
    'team.role.audit': 'Responsable Audit et certification, CPA',

    // testimonials
    'test.eyebrow': 'Plus de 4 200 clients',
    'test.titlePre': 'Ne nous croyez pas —',
    'test.titleAccent': 'écoutez-les',
    'test.retention': 'Taux de fidélisation',

    // cases
    'cases.eyebrow': 'Études de cas',
    'cases.titlePre': 'Nous travaillons avec passion pour répondre à vos',
    'cases.titleAccent': 'besoins',

    // faq
    'faq.eyebrow': 'FAQ',
    'faq.titlePre': 'Nous répondons toujours à',
    'faq.titleAccent': 'doutes',
    'faq.your': 'vos',
    'faq.desc':
      'Quelques-unes des questions les plus fréquentes de nos clients, nouveaux et existants. La vôtre n’y est pas ? Écrivez-nous et nous répondons le jour même.',

    // contact form
    'cf.eyebrow': 'Envoyez-nous un message',
    'cf.titlePre': 'Demandez à être',
    'cf.titleAccent': 'rappelé',
    'cf.first': 'Prénom',
    'cf.last': 'Nom',
    'cf.email': 'Courriel',
    'cf.help': 'Comment pouvons-nous vous aider ?',
    'cf.message': 'Votre message',
    'cf.errFill': 'Veuillez remplir votre nom, votre courriel et votre message',
    'cf.errCaptcha': 'Veuillez compléter la vérification',
    'cf.success': 'Merci ! Un conseiller WYSE Tribe vous contactera dans un délai d’un jour ouvrable.',

    // blog
    'blog.eyebrow': 'Notre blogue',
    'blog.titlePre': 'Derniers articles et',
    'blog.titleAccent': 'analyses',
    'blog.desc':
      'Des analyses claires sur la fiscalité canadienne, signées par nos spécialistes fiscaux. Moins de cinq minutes de lecture — zéro jargon.',

    // page headers
    'page.about': 'À propos de WYSE Tribe',
    'page.services': 'Nos services',
    'page.cases': 'Études de cas',
    'page.blog': 'WYSE Insights',
    'page.contact': 'Contacter WYSE Tribe',
    'page.crumb.about': 'À propos',
    'page.crumb.services': 'Services',
    'page.crumb.cases': 'Études',
    'page.crumb.blog': 'Blogue',
    'page.crumb.contact': 'Contact',

    // contact page
    'contact.eyebrow': 'Prenez contact',
    'contact.titlePre': 'Réservez votre',
    'contact.titleAccent': 'appel stratégique gratuit de 30 minutes',
    'contact.desc':
      'Parlez-nous brièvement de votre situation et un spécialiste WYSE Tribe vous contactera dans un délai d’un jour ouvrable. Pas de discours commercial — uniquement de vraies réponses.',

    // team
    'team.eyebrow': 'Notre équipe',
    'team.titlePre': 'Rencontrez les personnes derrière',
    'team.titleAccent': 'WYSE Tribe',

    // footer
    'footer.tagline':
      'La fiscalité canadienne à la manière WYSE — déclarations personnelles, corporatives et transfrontalières, conseil toute l’année et représentation confiante auprès de l’ARC.',
    'footer.quick': 'Liens rapides',
    'footer.useful': 'Liens utiles',
    'footer.contact': 'Contact',
    'footer.copy': 'WYSE Tribe — une marque de WYSETribe.ca. Tous droits réservés.',
    'footer.cpa': 'Représentant autorisé par l’ARC',

    // ---- Member dashboard (AccountPage) ----
    'acct.eyebrow': 'Votre compte',
    'acct.hi': 'Bonjour',
    'acct.docVault': 'Coffre de documents',
    'acct.taxFile': 'Dossier fiscal',
    'acct.bookNew': 'Prendre un nouveau rendez-vous',
    'acct.signOut': 'Déconnexion',
    'acct.upcoming': 'Prochains rendez-vous',
    'acct.loading': 'Chargement…',
    'acct.noUpcoming': 'Aucun rendez-vous à venir pour le moment.',
    'acct.bookFirst': 'Réservez votre premier rendez-vous',
    'acct.history': 'Historique',
    'acct.noPast': 'Aucun rendez-vous passé.',
    'acct.mySpecialist': 'Mon spécialiste fiscal',
    'acct.noSpec': 'Pas encore de spécialiste',
    'acct.noSpecDesc': 'Votre première réservation en assignera un.',
    'acct.profile': 'Profil',
    'acct.edit': 'Modifier',
    'acct.cancel': 'Annuler',
    'acct.fullName': 'Nom complet',
    'acct.phonePh': 'Téléphone',
    'acct.save': 'Enregistrer',
    'acct.role': 'Rôle',
    'acct.helpTitle': 'Besoin d’aide ?',
    'acct.helpBody': 'Contactez notre équipe à',
    'acct.offersDesc': 'vous a envoyé une offre personnelle. Examinez-la, acceptez ou refusez →',
    'acct.offersSpecFallback': 'Un spécialiste',
    'acct.offersWants': 'souhaite travailler avec vous',
    'acct.offersWantPlural': 'souhaitent travailler avec vous',
    'acct.taxSpecialist': 'Spécialiste fiscal',
    'acct.taxSpecialistPlural': 'Spécialistes fiscaux',
    'acct.emailNotifsTitle': 'Notifications par courriel',
    'acct.emailNotifsBody': 'Choisissez les mises à jour qui arrivent dans votre boîte de réception.',
    'acct.profileUpdated': 'Profil mis à jour',
    'acct.profileErr': 'Impossible de mettre à jour le profil',
    'acct.manage': 'Gérer',
    'acct.with': 'avec',

    // ---- Login / Signup ----
    'login.back': 'Retour à wysetribe.ca',
    'login.eyebrow': 'Espace client',
    'login.heroTitle1': 'Votre monde fiscal,',
    'login.heroTitle2': 'toujours à portée',
    'login.heroDesc': 'Réservez et gérez vos rendez-vous, partagez vos documents en toute sécurité et discutez avec votre conseiller WYSE Tribe dédié — le tout depuis un seul endroit.',
    'login.feat1': 'Réservation et reprogrammation instantanées',
    'login.feat2': 'Accès à un conseiller toute l’année',
    'login.feat3': 'Portail de documents sécurisé',
    'login.feat4': 'Rappels par courriel avant chaque rendez-vous',
    'login.signinTab': 'Connexion',
    'login.signupTab': 'S’inscrire',
    'login.welcomeBack': 'Bon retour',
    'login.createAcct': 'Créez votre compte',
    'login.signinSub': 'Connectez-vous pour gérer vos rendez-vous.',
    'login.signupSub': 'Inscrivez-vous pour réserver et suivre vos rendez-vous avec WYSE Tribe.',
    'login.invited': 'Vous êtes invité',
    'login.invitedBy': 'par',
    'login.invitedLock': 'Le courriel est verrouillé à votre invitation.',
    'login.phonePh': 'Téléphone (optionnel)',
    'login.passwordPh': 'Mot de passe',
    'login.passwordCreatePh': 'Créez un mot de passe (min 12 caractères)',
    'login.emailPh': 'Courriel',
    'login.remember': 'Se souvenir de moi',
    'login.forgot': 'Mot de passe oublié ?',
    'login.signinBtn': 'Se connecter',
    'login.createBtn': 'Créer un compte',
    'login.newHere': 'Nouveau ici ?',
    'login.haveAcct': 'Vous avez déjà un compte ?',
    'login.createOne': 'Créer un compte',
    'login.welcomeToast': 'Bon retour,',
    'login.acctCreated': 'Compte créé !',
    'login.errLogin': 'Échec de la connexion',
    'login.errSignup': 'Échec de l’inscription',
    'login.showPw': 'Afficher le mot de passe',
    'login.hidePw': 'Masquer le mot de passe',
    'login.inviteErr1': 'Cette invitation ne concerne pas l’inscription d’un membre.',
    'login.inviteErr2': 'Cette invitation a expiré. Veuillez en demander une nouvelle.',
    'login.inviteErr3': 'Cette invitation a déjà été utilisée.',
    'login.inviteErr4': 'Cette invitation a été révoquée.',
    'login.inviteErr5': 'Le lien d’invitation est invalide.',

    // ---- Forgot / Verify / Reset password ----
    'fp.back': 'Retour à la connexion',
    'fp.eyebrow': 'Sécurité du compte',
    'fp.title': 'Mot de passe oublié ?',
    'fp.desc': 'Saisissez votre courriel et nous vous enverrons un code de vérification à 6 chiffres pour la réinitialiser.',
    'fp.emailPh': 'Adresse courriel',
    'fp.send': 'Envoyer le code de vérification',
    'fp.checkInbox': 'Vérifiez votre boîte de réception',
    'fp.footer': 'Pour votre sécurité, nous n’envoyons un code que si votre courriel correspond à un compte existant. Nous ne révélons jamais si un compte existe.',
    'fp.errMissing': 'Veuillez entrer votre courriel',
    'fp.errSend': 'Envoi impossible. Veuillez réessayer.',
    'vc.back': 'Changer de courriel',
    'vc.eyebrow': 'Vérifiez votre identité',
    'vc.title': 'Saisissez le code de vérification',
    'vc.desc1': 'Nous avons envoyé un code à 6 chiffres à',
    'vc.desc2': 'Le code expire dans 15 minutes.',
    'vc.verify': 'Vérifier et continuer',
    'vc.resendIn': 'Renvoyer le code dans',
    'vc.resend': 'Renvoyer le code',
    'vc.enterCode': 'Saisissez le code à 6 chiffres',
    'vc.verifyFailed': 'Échec de la vérification',
    'vc.resendOk': 'Un nouveau code est en route.',
    'vc.resendErr': 'Renvoi impossible',
    'rp.back': 'Recommencer',
    'rp.eyebrow': 'Étape finale',
    'rp.title': 'Définissez un nouveau mot de passe',
    'rp.desc': 'Choisissez-le solide — au moins 12 caractères avec majuscules, minuscules, chiffre et symbole.',
    'rp.newPw': 'Nouveau mot de passe',
    'rp.confirmPw': 'Confirmer le nouveau mot de passe',
    'rp.strength': 'Force',
    'rp.sig0': 'Trop faible',
    'rp.sig1': 'Faible',
    'rp.sig2': 'Correct',
    'rp.sig3': 'Bon',
    'rp.sig4': 'Fort',
    'rp.sig5': 'Très fort',
    'rp.req1': '12+ caractères',
    'rp.req2': 'Lettre majuscule',
    'rp.req3': 'Lettre minuscule',
    'rp.req4': 'Chiffre',
    'rp.req5': 'Caractère spécial',
    'rp.req6': 'Les mots de passe correspondent',
    'rp.revoke': 'Me déconnecter de tous les autres appareils',
    'rp.submit': 'Mettre à jour le mot de passe',
    'rp.errPolicy': 'Le mot de passe ne respecte pas les exigences',
    'rp.errMatch': 'Les mots de passe ne correspondent pas',
    'rp.successToast': 'Mot de passe mis à jour.',
    'rp.successTitle': 'Mot de passe mis à jour',
    'rp.successBody': 'Votre mot de passe a été modifié avec succès. Connexion en cours…',
    'rp.errReset': 'Impossible de réinitialiser le mot de passe',

    // ---- Tax Filing Progress Stepper (member tracker) ----
    'tracker.eyebrow': 'Progression de votre déclaration',
    'tracker.taxYear': 'Année fiscale',
    'tracker.step': 'Étape',
    'tracker.of': 'sur',
    'tracker.gettingStarted': 'Démarrage',
    'tracker.specialistPrefix': 'Votre spécialiste :',
    'tracker.whereYouAre': 'Où vous en êtes',
    'tracker.currently': 'Actuellement :',
    'tracker.specialistNotes': 'Notes du spécialiste',
    'tracker.completed': 'Terminé',
    'tracker.actionPill': 'Action',
    'tracker.inProgress': 'En cours',
    'tracker.uploadDocs': 'Téléverser des documents',
    'tracker.reviewSign': 'Réviser et signer',
    'tracker.viewTaxFile': 'Voir le dossier complet',
    'tracker.noFile': 'Aucune déclaration en cours.',
    'tracker.p.intake': 'Accueil du client',
    'tracker.p.intake.desc': 'Nous avons votre intake — votre dossier est officiellement ouvert.',
    'tracker.p.documents': 'Documents reçus',
    'tracker.p.documents.desc': 'Nous recueillons (et avons reçu) les documents nécessaires à votre déclaration.',
    'tracker.p.review': 'Révision terminée',
    'tracker.p.review.desc': 'Votre spécialiste révise vos documents et prépare votre déclaration.',
    'tracker.p.signature': 'Signature demandée',
    'tracker.p.signature.desc': 'Veuillez réviser et signer votre déclaration afin que nous puissions la soumettre.',
    'tracker.p.filed_ready': 'Prête à transmettre',
    'tracker.p.filed_ready.desc': 'Votre déclaration signée est prête à être transmise à l’ARC.',
    'tracker.p.cra': 'Transmise à l’ARC',
    'tracker.p.cra.desc': 'Votre déclaration a été transmise à l’Agence du revenu du Canada.',
  },
  zh: {
    'top.home': '首页',
    'top.wysetribe': 'WYSE Tribe',
    'top.locations': '卡尔加里 · 多伦多',
    'top.signIn': '登录',
    'top.dashboard': '我的主页',
    'top.signOut': '退出登录',
    'top.language': '语言',
    'brand.sub': '会计 · 税务咨询',
    'nav.home': '首页',
    'nav.about': '关于我们',
    'nav.services': '服务',
    'nav.specialists': '税务专家',
    'spec.eyebrow': '我们的税务专家',
    'spec.heroTitle': '认识将为您处理',
    'spec.heroAccent': '报税季的专家',
    'spec.heroSubtitle': '浏览我们值得 CRA 信赖的双语税务专家团队。按您希望使用的语言筛选，然后直接预约最适合您的专家。',
    'spec.searchPlaceholder': '按姓名、职位或简介搜索…',
    'spec.languageLabel': '语言',
    'spec.langAny': '不限',
    'spec.empty': '暂无符合筛选条件的专家。',
    'spec.viewProfile': '查看资料',
    'spec.backToDirectory': '返回全部专家',
    'spec.titleFallback': '税务专家',
    'spec.bookWith': '预约',
    'spec.about': '简介',
    'spec.certifications': '资质与会员资格',
    'spec.services': '提供的服务',
    'spec.workingHours': '工作时间',
    'spec.workingHoursDesc': '周一至周五 上午 9 点 – 下午 5 点',
    'spec.workingHoursHint': '在线预约以查看实时空档。',
    'spec.preferTitle': '偏好这位专家？',
    'spec.preferDesc': '您可以在会员账户中将其设为首选专家。',
    'spec.managePrefs': '管理偏好设置',
    'spec.notFound': '未找到该专家',
    'spec.notFoundDesc': '此资料可能已移动或已停用。',
    'nav.cases': '案例',
    'nav.blog': '博客',
    'nav.contact': '联系我们',
    'nav.pages': '页面',
    'cta.bookAppointment': '预约咨询',
    'cta.getStarted': '立即开始',
    'cta.callUs': '立即致电',
    'cta.contactUs': '联系我们',
    'cta.allServices': '全部服务',
    'cta.allArticles': '全部文章',
    'cta.learnMore': '了解更多',
    'cta.talkToAdvisor': '咨询顾问',
    'cta.readArticle': '阅读文章',
    'cta.submitNow': '立即提交',
    'cta.submitted': '已提交',
    'cta.watchStory': '观看我们的故事',
    'hero.eyebrow': '欢迎来到 WYSE Tribe',
    'hero.titlePre': '报税比您',
    'hero.titleBreak': '想象的',
    'hero.titleAccent': '更简单',
    'hero.desc': '个人、企业及跨境税务，由真正会接听电话的税务专家为您处理。WYSE Tribe 将全年咨询与云端报税相结合——让您留住更多辛苦所得。',
    'hero.trusted': '深受 4,200 多位加拿大纳税人信赖',
    'hl.expert.title': '专业顾问',
    'hl.expert.text': '拥有 18 年以上加拿大税务经验的税务专家。',
    'hl.consulting.title': '税务咨询',
    'hl.consulting.text': '个人、企业及跨境税务规划，助您实现最大回报。',
    'hl.planning.title': '财务规划',
    'hl.planning.text': '围绕您的目标与人生阶段量身打造的全面财富策略。',
    'about.eyebrow': '我们是税务顾问',
    'about.titlePre': '我们为您提供最优质的服务，让您更加',
    'about.titleAccent': '便捷',
    'about.desc': 'WYSE Tribe 的创立理念很简单：税务应成为积累财富的工具，而不是每年一次的手忙脚乱。我们的税务专家将深厚的 CRA 专业知识与云端工作流程相结合，让每位客户都能获得清晰的答案、切实的规划，杜绝任何意外。',
    'about.advisors': '32+ 位认证顾问',
    'about.cpaLine': 'CRA 授权',
    'about.b.cra': 'CRA 授权专家',
    'about.b.pricing': '透明固定收费',
    'about.b.year': '全年税务支持',
    'about.b.cross': '跨境税务专长',
    'about.b.audit': '税务审计代理',
    'about.b.secure': '保密且安全',
    'about.founder': '创始人 · CPA',
    'stats.clients': '满意客户',
    'stats.returns': '已提交申报',
    'stats.years': '从业年限',
    'stats.experts': '税务专家',
    'comfort.titlePre': '我们的方案让您在税务管理中',
    'comfort.titleAccent': '更加安心',
    'comfort.titlePost': '',
    'comfort.desc': '从首次入门通话到报税后回顾，每一步都清晰规划。您将随时了解税务进展以及下一步安排。',
    'comfort.f1.t': '专属税务专家',
    'comfort.f1.d': '一位对您的档案了如指掌的顾问。',
    'comfort.f2.t': '全年支持',
    'comfort.f2.d': '每季度跟进，而不仅限于报税季。',
    'comfort.f3.t': 'CRA 代理',
    'comfort.f3.d': '我们处理每一封信函、每一次审查与审计。',
    'serv.eyebrow': '服务',
    'serv.titlePre': '我们的服务',
    'serv.titleAccent': '包括',
    'serv.desc': '个人申报、企业申报、审计辩护及跨境专项业务——一站式办理，全年无休。',
    'serv.pageEyebrow': '我们的业务',
    'serv.pageTitlePre': '满足您税务生活所需的',
    'serv.pageTitleAccent': '每项服务',
    'serv.pageDesc': '从简单的 T1 到多实体、多国家架构——WYSE Tribe 都以同样的用心处理。',
    'service.personal-tax.title': '个人报税',
    'service.personal-tax.short': '准确、符合 CRA 规定的个人申报，助您获得最大退税。',
    'service.personal-tax.desc': '从 T4 雇员到自雇承包商，我们用心处理每一份加拿大个人申报。我们会找出每一项符合条件的扣除和抵免，让您留住更多收入。',
    'service.corporate-tax.title': '企业税务',
    'service.corporate-tax.short': 'T2 申报、年终规划及节税架构。',
    'service.corporate-tax.desc': '战略性企业税务规划、T2 申报、GST/HST 申报及 CRA 往来信函全程处理，让您的企业专注于发展。',
    'service.audit-assurance.title': '审计与鉴证',
    'service.audit-assurance.short': '可靠的 CRA 审计代理及财务报表审阅。',
    'service.audit-assurance.desc': '我们的审计专家直接代表您与 CRA 沟通，让您高枕无忧。我们准备文件、协商结果并保护您的立场。',
    'service.bookkeeping.title': '记账服务',
    'service.bookkeeping.short': '清晰账目、月度报告及云端工作流程。',
    'service.bookkeeping.desc': '集成 QuickBooks 和 Xero 的月度记账、工资处理、银行对账以及真正易懂的管理报告。',
    'service.cross-border.title': '跨境税务',
    'service.cross-border.short': '加美申报、FBAR 及税收协定优化。',
    'service.cross-border.desc': '美加跨境申报、FBAR、FATCA、协定立场及海外人士规划。我们让双重申报简单且合规。',
    'service.financial-planning.title': '财务规划',
    'service.financial-planning.short': 'RRSP、TFSA、退休及遗产策略，助财富复利增长。',
    'service.financial-planning.desc': '涵盖投资、退休、保险及遗产的完整规划——与您的税务状况保持一致，让每一分钱发挥更大作用。',
    'case.cat.corporate': '企业',
    'case.cat.family-office': '家族办公室',
    'case.cat.personal': '个人',
    'case.cat.cross-border': '跨境',
    'case.short-range-tax-planning.title': '短期税务规划',
    'case.short-range-tax-planning.summary': '为一家多伦多机构进行年终优化，单一财年内应缴税款减少 28%。',
    'case.purposive-tax-planning.title': '目的性税务规划',
    'case.purposive-tax-planning.summary': '为一位多代家族客户设立家族信托及控股公司，以节税方式实现财富复利增长。',
    'case.permissive-tax-planning.title': '许可性税务规划',
    'case.permissive-tax-planning.summary': '为一位自雇创作者充分利用 CRA 允许的每项抵免和扣除，退税增加 $14,800。',
    'case.cross-border-restructure.title': '跨境架构重组',
    'case.cross-border-restructure.summary': '为一位回国的海外人士理清 5 年美加申报积压，零罚款。',
    'testimonial.1.role': '创始人，Northway Studio',
    'testimonial.1.quote': 'WYSE Tribe 重组了我们的公司，第一年就为我们节省了近 $22k。他们用通俗易懂的语言解释一切——这在业内实属难得。',
    'testimonial.2.role': '自雇顾问',
    'testimonial.2.quote': '从大型连锁报税店转过来后再也不想回去。我的申报更精准，退税更多，而且我真正清楚申报了什么以及原因。',
    'testimonial.3.role': '房地产投资者',
    'testimonial.3.quote': '他们负责三家公司以及我的个人申报。积极主动、响应迅速，总是比 CRA 领先一步。',
    'testimonial.4.role': '美加海外人士',
    'testimonial.4.quote': '跨境申报曾经是一场噩梦。WYSE Tribe 让它变得平淡无奇——以最好的方式。',
    'faq.1.q': 'WYSE Tribe 多快能提交我的个人申报？',
    'faq.1.a': '对于简单的 T1 申报，我们通常在收到您的文件后 3 个工作日内提交。涉及自雇、租金或投资收入的复杂申报将在 7–10 个工作日内完成。',
    'faq.2.q': '你们的记账服务同时支持 QuickBooks 和 Xero 吗？',
    'faq.2.a': '是的。我们的记账团队拥有 QuickBooks Online 和 Xero 认证，必要时也可使用 FreshBooks、Wave 及定制 ERP。',
    'faq.3.q': '如果在你们提交后 CRA 对我进行审计怎么办？',
    'faq.3.a': '我们会直接代表您。每位 WYSE Tribe 客户对我们准备的任何申报均可获得免费的 CRA 信函支持，包括处理预评估审查和全面审计。',
    'faq.4.q': '你们能处理美国和加拿大的跨境税务申报吗？',
    'faq.4.a': '当然可以。我们的跨境团队同步提交美国 1040、FBAR、FATCA 以及加拿大 T1/T2 申报，并运用税收协定立场避免双重征税。',
    'faq.5.q': 'WYSE Tribe 与大型连锁报税店有何不同？',
    'faq.5.a': '我们将全年咨询与安全的云端工作流程相结合。您将获得专属顾问、透明定价和主动规划——而不是一年一次的交易。',
    'blog.cat.personal': '个人',
    'blog.cat.corporate': '企业',
    'blog.cat.audit': '审计',
    'blog.2025-tfsa-contribution-room.title': '2025 年 TFSA 供款额度——有哪些变化',
    'blog.2025-tfsa-contribution-room.excerpt': 'TFSA 额度再次调整。以下是加拿大储蓄者本季度应采取的措施，以充分利用新额度。',
    'blog.corporate-class-or-holdco.title': '公司类基金还是控股公司？创始人指南',
    'blog.corporate-class-or-holdco.excerpt': '哪种架构税后财富增长更快——哪种会悄然侵蚀回报。我们为您详细计算。',
    'blog.cra-audit-defence-2025.title': '2025 年 CRA 审计辩护七步攻略',
    'blog.cra-audit-defence-2025.excerpt': '审计数量同比增长 19%。在任何信函到达之前，使用这份清单让您的记录无懈可击。',
    'team.role.founder': '创始人兼首席税务顾问，CPA',
    'team.role.corporate': '企业税务总监，CPA',
    'team.role.audit': '审计与鉴证主管，CPA',
    'test.eyebrow': '超过 4,200 位客户',
    'test.titlePre': '不必只听我们说——',
    'test.titleAccent': '听听他们怎么说',
    'test.retention': '客户留存率',
    'cases.eyebrow': '案例研究',
    'cases.titlePre': '我们全心全意满足您的',
    'cases.titleAccent': '需求',
    'faq.eyebrow': '常见问题',
    'faq.titlePre': '我们随时解答',
    'faq.titleAccent': '疑问',
    'faq.your': '您的',
    'faq.desc': '以下是新老 WYSE Tribe 客户最常见的一些问题。没有找到您的问题？给我们留言，我们当天回复。',
    'cf.eyebrow': '发送留言',
    'cf.titlePre': '申请',
    'cf.titleAccent': '回电',
    'cf.first': '名',
    'cf.last': '姓',
    'cf.email': '电子邮箱',
    'cf.help': '我们能为您做什么？',
    'cf.message': '您的留言',
    'cf.errFill': '请填写您的姓名、电子邮箱和留言',
    'cf.errCaptcha': '请完成人机验证',
    'cf.success': '谢谢！WYSE Tribe 顾问将在 1 个工作日内与您联系。',
    'blog.eyebrow': '我们的博客',
    'blog.titlePre': '最新博客与',
    'blog.titleAccent': '文章',
    'blog.desc': '由我们的税务专家撰写的加拿大税务精辟见解。阅读时间不到五分钟——零术语。',
    'page.about': '关于 WYSE Tribe',
    'page.services': '我们的服务',
    'page.cases': '客户案例',
    'page.blog': 'WYSE 洞察',
    'page.contact': '联系 WYSE Tribe',
    'page.crumb.about': '关于我们',
    'page.crumb.services': '服务',
    'page.crumb.cases': '案例',
    'page.crumb.blog': '博客',
    'page.crumb.contact': '联系我们',
    'contact.eyebrow': '联系我们',
    'contact.titlePre': '预约您的免费',
    'contact.titleAccent': '30 分钟策略咨询',
    'contact.desc': '简单介绍一下您的情况，WYSE Tribe 专家将在一个工作日内与您联系。没有推销——只有真实的答案。',
    'team.eyebrow': '我们的团队',
    'team.titlePre': '认识背后的团队',
    'team.titleAccent': 'WYSE Tribe',
    'footer.tagline': '明智的加拿大税务——个人、企业及跨境申报，全年咨询，以及值得信赖的 CRA 代理。',
    'footer.quick': '快速链接',
    'footer.useful': '实用链接',
    'footer.contact': '联系方式',
    'footer.copy': 'WYSE Tribe – WYSETribe.ca 旗下品牌。保留所有权利。',
    'footer.cpa': 'CRA 授权代表',
    'acct.eyebrow': '您的账户',
    'acct.hi': '您好',
    'acct.docVault': '文件库',
    'acct.taxFile': '税务档案',
    'acct.bookNew': '预约新咨询',
    'acct.signOut': '退出登录',
    'acct.upcoming': '即将到来的预约',
    'acct.loading': '加载中…',
    'acct.noUpcoming': '暂无即将到来的预约。',
    'acct.bookFirst': '预约您的首次咨询',
    'acct.history': '历史记录',
    'acct.noPast': '暂无过往预约。',
    'acct.mySpecialist': '我的税务专家',
    'acct.noSpec': '尚未分配专家',
    'acct.noSpecDesc': '您的首次预约将为您分配一位专家。',
    'acct.profile': '个人资料',
    'acct.edit': '编辑',
    'acct.cancel': '取消',
    'acct.fullName': '全名',
    'acct.phonePh': '电话',
    'acct.save': '保存',
    'acct.role': '角色',
    'acct.helpTitle': '需要帮助？',
    'acct.helpBody': '联系我们的团队：',
    'acct.offersDesc': '向您发送了个人邀请。查看并接受或拒绝 →',
    'acct.offersSpecFallback': '一位专家',
    'acct.offersWants': '希望与您合作',
    'acct.offersWantPlural': '希望与您合作',
    'acct.taxSpecialist': '税务专家',
    'acct.taxSpecialistPlural': '税务专家',
    'acct.emailNotifsTitle': '电子邮件通知',
    'acct.emailNotifsBody': '选择您希望接收的邮件更新。',
    'acct.profileUpdated': '个人资料已更新',
    'acct.profileErr': '无法更新个人资料',
    'acct.manage': '管理',
    'acct.with': '与',
    'login.back': '返回 wysetribe.ca',
    'login.eyebrow': '客户门户',
    'login.heroTitle1': '您的税务世界，',
    'login.heroTitle2': '触手可及',
    'login.heroDesc': '预约和管理咨询、安全共享文件，并与您的专属 WYSE Tribe 顾问沟通——一站式完成。',
    'login.feat1': '即时预约与改期',
    'login.feat2': '全年顾问服务',
    'login.feat3': '安全文件门户',
    'login.feat4': '每次预约前发送邮件提醒',
    'login.signinTab': '登录',
    'login.signupTab': '注册',
    'login.welcomeBack': '欢迎回来',
    'login.createAcct': '创建您的账户',
    'login.signinSub': '登录以管理您的预约。',
    'login.signupSub': '注册以预约并跟踪 WYSE Tribe 的咨询。',
    'login.invited': '您已受邀',
    'login.invitedBy': '邀请人：',
    'login.invitedLock': '电子邮箱已锁定为您的邀请邮箱。',
    'login.phonePh': '电话（可选）',
    'login.passwordPh': '密码',
    'login.passwordCreatePh': '创建密码（至少 12 个字符）',
    'login.emailPh': '电子邮箱',
    'login.remember': '记住我',
    'login.forgot': '忘记密码？',
    'login.signinBtn': '登录',
    'login.createBtn': '创建账户',
    'login.newHere': '新用户？',
    'login.haveAcct': '已有账户？',
    'login.createOne': '创建账户',
    'login.welcomeToast': '欢迎回来，',
    'login.acctCreated': '账户已创建！',
    'login.errLogin': '登录失败',
    'login.errSignup': '注册失败',
    'login.showPw': '显示密码',
    'login.hidePw': '隐藏密码',
    'login.inviteErr1': '此邀请不适用于会员注册。',
    'login.inviteErr2': '此邀请已过期，请申请新的邀请。',
    'login.inviteErr3': '此邀请已被使用。',
    'login.inviteErr4': '此邀请已被撤销。',
    'login.inviteErr5': '邀请链接无效。',
    'fp.back': '返回登录',
    'fp.eyebrow': '账户安全',
    'fp.title': '忘记密码？',
    'fp.desc': '输入您的电子邮箱，我们将发送 6 位验证码以重置密码。',
    'fp.emailPh': '电子邮箱地址',
    'fp.send': '发送验证码',
    'fp.checkInbox': '请查看您的收件箱',
    'fp.footer': '为了您的安全，只有当您的电子邮箱与已注册账户匹配时，我们才会发送验证码。我们绝不透露账户是否存在。',
    'fp.errMissing': '请输入您的电子邮箱',
    'fp.errSend': '发送失败，请重试。',
    'vc.back': '更改电子邮箱',
    'vc.eyebrow': '验证您的身份',
    'vc.title': '输入验证码',
    'vc.desc1': '我们已将 6 位验证码发送至',
    'vc.desc2': '验证码将在 15 分钟后过期。',
    'vc.verify': '验证并继续',
    'vc.resendIn': '重新发送验证码倒计时',
    'vc.resend': '重新发送验证码',
    'vc.enterCode': '请输入 6 位验证码',
    'vc.verifyFailed': '验证失败',
    'vc.resendOk': '新的验证码已发送。',
    'vc.resendErr': '无法重新发送',
    'rp.back': '重新开始',
    'rp.eyebrow': '最后一步',
    'rp.title': '设置新密码',
    'rp.desc': '请设置强密码——至少 12 个字符，包含大小写字母、数字和符号。',
    'rp.newPw': '新密码',
    'rp.confirmPw': '确认新密码',
    'rp.strength': '强度',
    'rp.sig0': '太弱',
    'rp.sig1': '弱',
    'rp.sig2': '一般',
    'rp.sig3': '良好',
    'rp.sig4': '强',
    'rp.sig5': '非常强',
    'rp.req1': '12 个以上字符',
    'rp.req2': '大写字母',
    'rp.req3': '小写字母',
    'rp.req4': '数字',
    'rp.req5': '特殊字符',
    'rp.req6': '两次密码一致',
    'rp.revoke': '从所有其他设备退出登录',
    'rp.submit': '更新密码',
    'rp.errPolicy': '密码不符合要求',
    'rp.errMatch': '两次输入的密码不一致',
    'rp.successToast': '密码已更新。',
    'rp.successTitle': '密码已更新',
    'rp.successBody': '您的密码已成功更改。正在为您登录…',
    'rp.errReset': '无法重置密码',
    'tracker.eyebrow': '报税进度',
    'tracker.taxYear': '纳税年度',
    'tracker.step': '第',
    'tracker.of': '步，共',
    'tracker.gettingStarted': '准备开始',
    'tracker.specialistPrefix': '您的专家：',
    'tracker.whereYouAre': '当前进度',
    'tracker.currently': '当前：',
    'tracker.specialistNotes': '专家备注',
    'tracker.completed': '已完成',
    'tracker.actionPill': '待处理',
    'tracker.inProgress': '进行中',
    'tracker.uploadDocs': '上传文件',
    'tracker.reviewSign': '审阅并签署',
    'tracker.viewTaxFile': '查看完整税务档案',
    'tracker.noFile': '尚未开始报税。',
    'tracker.p.intake': '客户资料已受理',
    'tracker.p.intake.desc': '我们已收到您的资料——您的档案已正式开立。',
    'tracker.p.documents': '文件已收到',
    'tracker.p.documents.desc': '我们正在收集（并已收到）您申报所需的文件。',
    'tracker.p.review': '审核完成',
    'tracker.p.review.desc': '您的专家正在审核您的文件并准备申报。',
    'tracker.p.signature': '请求签署',
    'tracker.p.signature.desc': '请审阅并签署您的申报，以便我们提交。',
    'tracker.p.filed_ready': '准备提交',
    'tracker.p.filed_ready.desc': '您已签署的申报已准备好传送至 CRA。',
    'tracker.p.cra': '已提交 CRA',
    'tracker.p.cra.desc': '您的申报已传送至加拿大税务局（CRA）。',
  },

};

const LanguageContext = createContext({ lang: 'es', setLang: () => {}, t: (k) => k });

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => localStorage.getItem('wyse_lang') || 'es');
  useEffect(() => {
    localStorage.setItem('wyse_lang', lang);
    document.documentElement.lang = lang === 'zh' ? 'zh-CN' : lang;
  }, [lang]);
  const setLang = (l) => setLangState(l);
  const t = (key) => translations[lang][key] ?? translations.en[key] ?? key;
  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>{children}</LanguageContext.Provider>
  );
}

export const useLang = () => useContext(LanguageContext);
;
